import { Injectable } from '@angular/core';
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
import html2canvas from 'html2canvas';
import { environment } from '@env/environment';

@Injectable({
  providedIn: 'root'
})
export class PdfExportService {

  constructor() { }

  /**
   * Genera un PDF con:
   * - Una portada con datos básicos de la solicitud.
   * - Una página por cada imagen (incrustada centrada y con título).
   * - Las páginas de cada archivo PDF adjunto copiadas al documento final.
   * @param solicitud Objeto de la solicitud.
   * @param imgs Lista de nombres de imágenes.
   * @param files Lista de nombres de archivos PDF.
   * @param usuarioId ID del usuario.
   * @param nombresImagenes Mapa de nombres descriptivos para las imágenes.
   * @param fileName (Opcional) Nombre del archivo PDF resultante. Si no se provee, se genera automáticamente como "documentacion_<nro>.pdf".
   */
  async generateAttachmentsPDF(
    solicitud: any,
    imgs: string[],
    files: string[],
    usuarioId: string,
    nombresImagenes: { [key: string]: string },
    fileName?: string
  ): Promise<void> {
    const pdfDoc = await PDFDocument.create();
    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const fontSizeNormal = 12;

    // --- Portada ---
    let page = pdfDoc.addPage([595.28, 841.89]); // A4
    let { width, height } = page.getSize();
    page.drawText(`Documentación de Solicitud ${solicitud._nro}`, {
      x: 50, y: height - 80, size: 18, font, color: rgb(0, 0, 0),
    });
    page.drawText(`Titular: ${solicitud.usuario?.nombrePersona || 'N/A'} ${solicitud.usuario?.apellidoPersona || ''}`, {
      x: 50, y: height - 110, size: fontSizeNormal, font,
    });
    const fechaCarga = solicitud.inscripcion?.timeMark 
        ? new Date(solicitud.inscripcion.timeMark).toLocaleDateString('es-AR') 
        : 'No disponible';
    page.drawText(`Fecha de carga de documentación: ${fechaCarga}`, {
        x: 50, y: height - 130, size: fontSizeNormal, font,
    });

    // --- Imágenes ---
    for (const img of imgs) {
        const imgUrl = `${this.getBaseUrl()}/media/imgUserOffer/${usuarioId}/${img}`;
        try {
            const response = await fetch(imgUrl);
            const imgBytes = await response.arrayBuffer();

            const extension = img.split('.').pop()?.toLowerCase();
            const isWebp = extension === 'webp';

            let image;
            if (isWebp) {
                // Convertir webp a PNG
                const pngBytes = await this.convertToPng(imgBytes);
                image = await pdfDoc.embedPng(pngBytes);
            } else if (extension === 'png') {
                image = await pdfDoc.embedPng(imgBytes);
            } else {
                // asume jpg/jpeg u otros que pdf-lib pueda manejar como JPG
                image = await pdfDoc.embedJpg(imgBytes);
            }

            page = pdfDoc.addPage([595.28, 841.89]);
            ({ width, height } = page.getSize());

            const nombreImg = nombresImagenes[img] || img;
            page.drawText(nombreImg, {
                x: 50, y: height - 50, size: 14, font, color: rgb(0.2, 0.2, 0.2),
            });

            const maxWidth = width - 100;
            const maxHeight = height - 120;
            const dims = image.scaleToFit(maxWidth, maxHeight);
            page.drawImage(image, {
                x: (width - dims.width) / 2,
                y: height - 60 - dims.height,
                width: dims.width,
                height: dims.height,
            });
        } catch (e) {
            console.error(`Error al procesar imagen ${img}:`, e);
            page = pdfDoc.addPage();
            page.drawText(`Error al cargar: ${img}`, {
                x: 50, y: height - 100, size: 12, font, color: rgb(1, 0, 0),
            });
        }
    }

    // --- Archivos PDF ---
    for (const file of files) {
      const fileUrl = `${this.getBaseUrl()}/media/imgUserOffer/${usuarioId}/${file}`;
      try {
        const fileResponse = await fetch(fileUrl);
        const fileBuffer = await fileResponse.arrayBuffer();
        const externalDoc = await PDFDocument.load(fileBuffer);
        const copiedPages = await pdfDoc.copyPages(externalDoc, externalDoc.getPageIndices());
        copiedPages.forEach(p => pdfDoc.addPage(p));
      } catch (e) {
        console.error(`Error al procesar archivo ${file}:`, e);
        page = pdfDoc.addPage();
        page.drawText(`Error al cargar: ${file}`, {
          x: 50, y: height - 100, size: 12, font, color: rgb(1, 0, 0),
        });
      }
    }

    // --- Descargar el PDF generado ---
    const pdfBytes = await pdfDoc.save();
    // Si se proporciona un nombre de archivo personalizado, se usa; si no, el nombre por defecto.
    const nombreFinal = fileName || `documentacion_${solicitud._nro}.pdf`;
    this.downloadBlob(pdfBytes, nombreFinal, 'application/pdf');
  }

  /**
   * Genera un PDF con una página A4 por cada elemento HTML recibido.
   * Cada elemento se captura con html2canvas y la imagen resultante se incrusta
   * en un PDF creado con pdf-lib.
   *
   * Nota: se usa pdf-lib en lugar de jsPDF porque jsPDF agrega siempre un
   * /OpenAction en el catálogo, que antivirus como Avast/AVG marcan como falso
   * positivo (PDF: MalwareX-gen [Scam]).
   *
   * @param elements Elementos HTML a capturar, en orden de página.
   * @param fileName Nombre del archivo PDF de salida.
   */
  async generateScreenshotsPDF(elements: HTMLElement[], fileName: string): Promise<void> {
    const pdfDoc = await PDFDocument.create();
    pdfDoc.setTitle(fileName.replace(/\.pdf$/i, ''));
    pdfDoc.setAuthor('Signo Médico');
    pdfDoc.setCreator('Signo Médico');
    pdfDoc.setProducer('Signo Médico');
    pdfDoc.setCreationDate(new Date());

    const pageWidth = 595.28;   // ancho A4 en puntos
    const pageHeight = 841.89;  // alto A4 en puntos

    for (const element of elements) {
      element.style.display = 'block';

      const canvas = await html2canvas(element, {
        scale: 2,            // mejor resolución
        useCORS: true,
        logging: false
      });

      const pngDataUrl = canvas.toDataURL('image/png');
      const pngBytes = await (await fetch(pngDataUrl)).arrayBuffer();
      const image = await pdfDoc.embedPng(pngBytes);

      const page = pdfDoc.addPage([pageWidth, pageHeight]);
      const imgHeight = (image.height * pageWidth) / image.width;
      page.drawImage(image, {
        x: 0,
        y: pageHeight - imgHeight,
        width: pageWidth,
        height: imgHeight
      });
    }

    const pdfBytes = await pdfDoc.save();
    this.downloadBlob(pdfBytes, fileName, 'application/pdf');
  }

  // Helpers privados
  private getBaseUrl(): string {
      return environment.urlBackend;
  }

  private downloadBlob(data: Uint8Array, fileName: string, mimeType: string): void {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  private async convertToPng(imageBytes: ArrayBuffer): Promise<Uint8Array> {
      const blob = new Blob([imageBytes], { type: 'image/webp' }); // asumimos webp; también podría detectarse
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      // Obtener como PNG
      const pngDataUrl = canvas.toDataURL('image/png');
      const response = await fetch(pngDataUrl);
      return new Uint8Array(await response.arrayBuffer());
  }
}